import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { TIMEZONE_OPTIONS } from '../../../helper/constants';
import {
  convertArrayToSelectOptions,
  getOptionsByKeys,
  parseEnumToSelectOption,
  parseEnumValuesToOptions,
} from '../../../helper/enum-parser';
import {
  ToastHelper,
  ToastMessageType,
  ToastType,
  generateErrorMessageFromResponse,
} from '../../../helper/toast';
import {
  emailRegex,
  latitudeRegex,
  longitudeRegex,
  phoneNumberRegex,
  validateABN,
  validateJapanVAT,
  validateVAT,
} from '../../../helper/validation';
import api from '../../../lib/api';
import { queryClient } from '../../../query-client';
import {
  Country,
  Currency,
  Language,
  LocationDetails,
  TaxPricingOption,
  DefaultLanguage,
} from '../../../types/location';
import CustomInput from '../../elements/CustomInput';
import SelectInput from '../../elements/SelectInput';
import ToggleInput from '../../elements/ToggleInput';
import { FormFieldsInputGrid } from '../../styled-components/FormStyleComponents';
import FormSubmitFooter from '../FormSubmitFooter';

interface LocationDetailsFormProps {
  id: string;
  locationDataResponse: LocationDetails;
}

const LocationDetailsForm = ({
  id,
  locationDataResponse,
}: LocationDetailsFormProps) => {
  const LANGUAGE_OPTIONS = parseEnumToSelectOption(Language);
  const DEFAULT_LANGUAGE_OPTIONS = parseEnumToSelectOption(DefaultLanguage);

  const CURRENCY_OPTIONS = parseEnumValuesToOptions(Currency);
  const COUNTRY_OPTIONS = getOptionsByKeys(Country);

  const country = useMemo(
    () =>
      locationDataResponse?.data?.attributes?.formatted_address?.slice(-1)[0],
    [locationDataResponse],
  );
  const initialFormValues = useMemo(
    () =>
      ({
        name: locationDataResponse?.data?.attributes?.name,
        legalEntity: locationDataResponse?.data?.attributes?.legal_entity,
        region: locationDataResponse?.included?.[0].attributes?.name,
        address: locationDataResponse?.data?.attributes?.address_line1,
        city: locationDataResponse?.data?.attributes?.city,
        state: locationDataResponse?.data?.attributes?.state_province,
        zip: locationDataResponse?.data?.attributes?.postal_code,
        country: country,
        timezone: locationDataResponse?.data?.attributes?.timezone,
        latitude: locationDataResponse?.data?.attributes?.latitude,
        longitude: locationDataResponse?.data?.attributes?.longitude,
        emailAddress: locationDataResponse?.data?.attributes?.email_address,
        phoneNumber: locationDataResponse?.data?.attributes?.phone_number,
        primaryLanguage:
          locationDataResponse?.data?.attributes?.primary_language,
        defaultLanguage:
          locationDataResponse?.data?.attributes?.default_language,
        currency: locationDataResponse?.data?.attributes?.currency_code,
        businessNumber: locationDataResponse?.data?.attributes?.business_number,
        taxInclusivePricing:
          locationDataResponse?.data?.attributes?.use_tax_inclusive_pricing ===
          TaxPricingOption['Always Include Tax'],
      } as FieldValues),
    [locationDataResponse],
  );

  const Schema = zod
    .object({
      name: zod.string(),
      legalEntity: zod.string(),
      region: zod.string(),
      address: zod.string(),
      city: zod.string(),
      zip: zod.string(),
      country: zod.string(),
      state: zod.string(),
      timezone: zod.string(),
      latitude: zod
        .string()
        .min(1, 'This field is required')
        .regex(latitudeRegex, 'Invalid latitude format'),
      longitude: zod
        .string()
        .min(1, 'This field is required')
        .regex(longitudeRegex, 'Invalid longitude format'),
      emailAddress: zod
        .string()
        .min(1, 'This field is required')
        .regex(emailRegex, 'Invalid email format'),
      phoneNumber: zod
        .string()
        .min(1, 'This field is required')
        .regex(
          phoneNumberRegex,
          'Phone number must be entered in the format: "+999999999".',
        ),
      primaryLanguage: zod.string(),
      defaultLanguage: zod.string(),
      currency: zod.string(),
      businessNumber: zod.string(),
      taxInclusivePricing: zod.boolean(),
    })
    .superRefine(({ country, businessNumber }, ctx) => {
      if (
        country === 'Australia' &&
        !!businessNumber &&
        !validateABN(businessNumber)
      ) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Invalid AUS Business Number format',
          path: ['businessNumber'],
        });
      }

      if (
        country === 'United Kingdom' &&
        !!businessNumber &&
        !validateVAT(businessNumber)
      ) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Invalid Vat Number format',
          path: ['businessNumber'],
        });
      }

      if (
        country === 'Japan' &&
        !!businessNumber &&
        !validateJapanVAT(businessNumber)
      ) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Invalid Vat Number format',
          path: ['businessNumber'],
        });
      }
    });

  type FormSchemaType = zod.infer<typeof Schema>;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<FormSchemaType>({
    defaultValues: initialFormValues,
    resolver: zodResolver(Schema),
    mode: 'all',
  });

  const updateLocationData = useMutation({
    mutationFn: (data: FieldValues) =>
      api.fetch('update_location', {
        id: id,
        name: data.name,
        legal_entity: data?.legalEntity,
        region: locationDataResponse?.included?.[0].id,
        default_product_collection:
          locationDataResponse?.data?.relationships?.default_product_collection
            ?.data.id,
        phone_number: data.phoneNumber,
        email_address: data.emailAddress,
        latitude: data.latitude,
        longitude: data.longitude,
        primary_language: data.primaryLanguage,
        default_language: data.defaultLanguage,
        business_number: data.businessNumber.replace(/\s/g, ''),
        use_tax_inclusive_pricing: data.taxInclusivePricing
          ? TaxPricingOption['Always Include Tax']
          : locationDataResponse?.data.attributes?.use_tax_inclusive_pricing ===
            TaxPricingOption['Inherit from tenant']
          ? TaxPricingOption['Inherit from tenant']
          : TaxPricingOption['Always Exclude Tax'],
      }),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['get_location_by_id'] });
      ToastHelper.showToast(
        'Location',
        ToastType.SUCCESS,
        ToastMessageType.UPDATE_DETAILS,
      );
    },
    onError: (err) => {
      const errorMessage = generateErrorMessageFromResponse(err);

      ToastHelper.showToast(
        'Location',
        ToastType.ERROR,
        ToastMessageType.ERROR,
        errorMessage,
      );
    },
  });

  const onSubmit = (data: FieldValues) => {
    updateLocationData.mutate(data);
  };

  return (
    <div>
      <div className='w-full mt-5 p-[44px] bg-white  border border-gray-light rounded-lg'>
        <FormFieldsInputGrid>
          <CustomInput
            id='locationName'
            label='Location Name'
            disabled
            value={watch('name')}
            {...register('name')}
          />
          <CustomInput
            id='legalEntity'
            label='Legal Entity'
            value={watch('legalEntity')}
            {...register('legalEntity')}
          />
        </FormFieldsInputGrid>

        <FormFieldsInputGrid>
          <CustomInput
            id='address'
            label='Address'
            disabled
            value={watch('address')}
            {...register('address')}
          />

          <CustomInput
            id='city'
            label='City'
            disabled
            value={watch('city')}
            {...register('city')}
          />
        </FormFieldsInputGrid>

        <FormFieldsInputGrid>
          <CustomInput
            id='state'
            label='State/Province'
            disabled
            value={watch('state')}
            {...register('state')}
          />
          <CustomInput id='zip' label='Zip' disabled {...register('zip')} />
        </FormFieldsInputGrid>

        <FormFieldsInputGrid>
          <SelectInput
            label='Country'
            options={COUNTRY_OPTIONS}
            disabled
            value={watch('country')}
            {...register('country')}
          />
          <CustomInput
            id='region'
            label='Region'
            disabled
            value={watch('region')}
            {...register('region')}
          />
        </FormFieldsInputGrid>

        <FormFieldsInputGrid>
          <SelectInput
            label='Timezone'
            options={convertArrayToSelectOptions(TIMEZONE_OPTIONS)}
            disabled
            value={watch('timezone')}
            {...register('timezone')}
          />
          <CustomInput
            id='lat'
            label='Lat'
            required
            {...register('latitude')}
            value={watch('latitude')}
            error={errors['latitude']?.message}
          />
          <CustomInput
            id='long'
            label='Long'
            required
            {...register('longitude')}
            value={watch('longitude')}
            error={errors['longitude']?.message}
          />
        </FormFieldsInputGrid>

        <FormFieldsInputGrid>
          <CustomInput
            id='email'
            label='Email'
            required
            {...register('emailAddress')}
            value={watch('emailAddress')}
            error={errors['emailAddress']?.message}
          />
          <CustomInput
            id='phoneNumber'
            label='Phone Number'
            required
            {...register('phoneNumber')}
            value={watch('phoneNumber')}
            error={errors['phoneNumber']?.message}
          />
        </FormFieldsInputGrid>

        <FormFieldsInputGrid>
          <SelectInput
            label='Primary Language'
            options={LANGUAGE_OPTIONS}
            value={watch('primaryLanguage')}
            {...register('primaryLanguage')}
          />

          <SelectInput
            label='Default Language'
            options={DEFAULT_LANGUAGE_OPTIONS}
            value={watch('defaultLanguage')}
            {...register('defaultLanguage')}
          />
        </FormFieldsInputGrid>
        <FormFieldsInputGrid>
          <SelectInput
            label='Currency'
            options={CURRENCY_OPTIONS}
            disabled
            value={watch('currency')}
            {...register('currency')}
          />
          <ToggleInput
            id='taxInclusivePricing'
            label='Tax Inclusive Pricing'
            disabled={
              watch('country') !== 'United Kingdom' &&
              watch('country') !== 'Australia' &&
              watch('country') !== 'Singapore' &&
              watch('country') !== 'Japan'
                ? true
                : false
            }
            {...register('taxInclusivePricing')}
            checked={watch('taxInclusivePricing')}
          />
        </FormFieldsInputGrid>

        {watch('country') === 'Australia' && (
          <div className='w-1/2'>
            <CustomInput
              id='businessNumber'
              label='Australian Business Number'
              required
              value={watch('businessNumber')}
              {...register('businessNumber')}
              error={errors['businessNumber']?.message}
            />
          </div>
        )}

        {watch('country') === 'United Kingdom' && (
          <div className='w-1/2'>
            <CustomInput
              id='businessNumber'
              label='Vat Number'
              required
              value={watch('businessNumber')}
              {...register('businessNumber')}
              error={errors['businessNumber']?.message}
            />
          </div>
        )}

        {watch('country') === 'Japan' && (
          <div className='w-1/2'>
            <CustomInput
              id='businessNumber'
              label='Vat Number'
              required
              value={watch('businessNumber')}
              {...register('businessNumber')}
              error={errors['businessNumber']?.message}
            />
          </div>
        )}
      </div>
      <FormSubmitFooter
        handleSubmit={handleSubmit(onSubmit)}
        loading={updateLocationData.isPending}
        disabled={Object.keys(dirtyFields).length === 0}
      />
    </div>
  );
};

export default LocationDetailsForm;
